import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { API } from '../App';
import axios from 'axios';
import { Shield, CheckCircle, FileCheck, TrendingUp, Award } from 'lucide-react';

export default function PublicPassport() {
  const { token } = useParams();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicPassport();
  }, [token]);

  const fetchPublicPassport = async () => {
    try {
      const response = await axios.get(`${API}/passport/public/${token}`);
      setPassport(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Passport not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading passport...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Passport Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12" data-testid="public-passport">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              RefrX Verified Profile
            </h1>
            <p className="text-gray-600 mt-2">Digital Employability Passport</p>
          </div>

          {/* Profile Card */}
          <Card className="border-0 shadow-2xl">
            <CardHeader className="text-center pb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4">
                {passport.user.full_name.charAt(0)}
              </div>
              <CardTitle className="text-2xl mb-2">{passport.user.full_name}</CardTitle>
              <div className="flex items-center justify-center gap-2">
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  KYC {passport.user.kyc_status}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* TRUST-AI Score */}
              <div className="text-center py-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-900">TRUST-AI Score</span>
                </div>
                <div className="text-6xl font-bold gradient-text mb-2">
                  {passport.user.trust_score?.toFixed(1) || 'N/A'}
                </div>
                <p className="text-gray-500">out of 100</p>
                {passport.user.trust_score && (
                  <Progress value={passport.user.trust_score} className="mt-4 max-w-sm mx-auto h-2" />
                )}
              </div>

              {/* Verified Documents */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <FileCheck className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-900">{passport.verified_documents}</p>
                  <p className="text-xs text-blue-700">Verified Documents</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <Shield className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-900 capitalize">
                    {passport.user.kyc_status}
                  </p>
                  <p className="text-xs text-purple-700">KYC Status</p>
                </div>
              </div>

              {/* Score Breakdown */}
              {passport.score_breakdown && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-600" />
                    Score Breakdown
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(passport.score_breakdown).map(([key, value]) => (
                      <div key={key} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 capitalize mb-1">
                          {key.replace('_', ' ')}
                        </p>
                        <p className="text-lg font-semibold">
                          {typeof value === 'number' ? value.toFixed(1) : value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Analysis */}
              {passport.ai_analysis && (
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h4 className="font-semibold text-sm mb-2 text-indigo-900">AI Analysis</h4>
                  <p className="text-sm text-indigo-800">{passport.ai_analysis}</p>
                </div>
              )}

              {/* Footer */}
              <div className="pt-6 border-t text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>Verified by RefrX Platform</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}